import Cookies from 'js-cookie';

import { REFERRAL_CODE_STORAGE_KEY } from './hooks/useReferralCode';
import { TYourBusinessFormValues } from './YourBusiness/YourBusiness';
import { CustomerState } from '../../api/lending/edge';

export const HUBSPOT_DIRECTORS_PROFILE_FORM_URL =
  'https://api.hsforms.com/submissions/v3/integration/submit/6334445/0456b719-93db-4ac3-a393-2b968274c505';

export const HUBSPOT_YOUR_BUSINESS_FORM_URL =
  'https://api.hsforms.com/submissions/v3/integration/submit/6334445/ee39e3e6-c9f5-40b8-a983-ce66a4218d9c';

export function postDirectorsProfileToHubspot(customerState: CustomerState) {
  const utmParams = getUtmParams();

  const person = customerState?.application?.people?.[0];
  const company = customerState?.application?.company;
  const email = person?.emails && person.emails[0].email;
  const phoneNumber = person?.phones && person.phones[0].number;
  const residentialAddress =
    person?.residential_addresses && person.residential_addresses[0];

  const fields = [
    {
      name: 'title',
      value: person?.title || '',
    },
    {
      name: 'email',
      value: email,
    },
    {
      name: 'firstname',
      value: person?.first_name || '',
    },
    {
      name: 'lastname',
      value: person?.last_name || '',
    },
    {
      name: 'phone',
      value: '+44' + phoneNumber || '',
    },
    {
      name: 'company',
      value: company?.registered_company_name || '',
    },
    {
      name: 'company_number',
      value: company?.company_number || '',
    },
    {
      name: 'company_type',
      value: company?.type || '',
    },
    {
      name: 'address_line_1',
      value: `${residentialAddress?.house_number} ${residentialAddress?.street_line_1}`,
    },
    {
      name: 'address_line_2',
      value: residentialAddress?.street_line_2 || '',
    },
    {
      name: 'city',
      value: residentialAddress?.town || '',
    },
    {
      name: 'postcode',
      value: residentialAddress?.postcode || '',
    },
    {
      name: 'date_of_birth',
      value: person?.date_of_birth,
    },
    ...utmParams,
  ].filter(Boolean);

  const referralCode = getReferralCode();
  if (referralCode !== null) {
    fields.push({
      name: 'iwocapay_seller_referral_id',
      value: referralCode,
    });
  }

  return postToHubspot(HUBSPOT_DIRECTORS_PROFILE_FORM_URL, fields);
}

// Before submitting the event handler does some normalisation
// This type accounts for those changes
type TYourBusinessData = {
  email: string;
  accept_b2b_payments: boolean;
} & NonNullable<Omit<TYourBusinessFormValues, 'accept_b2b_payments'>>;

export function postYourBusinessToHubspot(yourBusinessData: TYourBusinessData) {
  const fieldToName = {
    email: yourBusinessData.email,
    iwocapay_annual_turnover: yourBusinessData.annual_turnover,
    iwocapay_how_many_b2b_payments_do_you_take_per_month:
      yourBusinessData.invoices_per_month,
    iwocapay_do_you_accept_b2b_payments_on_your_website:
      yourBusinessData.accept_b2b_payments,
    website: yourBusinessData.url_online_store,
    iwocapay_ecomm_platform_use: yourBusinessData.ecomm_platform_use,
    iwocapay_accountancy_platform_use:
      yourBusinessData.accountancy_platform_use,
    iwocapay_reason_for_use: yourBusinessData.reason_for_use,
  };

  const fields = Object.entries(fieldToName).map(([key, value]) => {
    const normalisedValue = Array.isArray(value) ? value.join(';') : value;

    return {
      name: key,
      value: normalisedValue,
    };
  });

  return postToHubspot(HUBSPOT_YOUR_BUSINESS_FORM_URL, fields);
}

function postToHubspot(
  formUrl: string,
  fields: { name: string; value: string | boolean | undefined }[],
) {
  if (window.location.origin !== 'https://www.iwoca.co.uk') {
    console.warn(
      'Posting to hubspot requires to be on "https://www.iwoca.co.uk"',
      fields,
    );
    return;
  }

  return fetch(formUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json;charset=UTF-8' },
    body: JSON.stringify({
      fields: fields,
      context: {
        hutk: Cookies.get('hubspotutk'),
      },
    }),
  });
}

function getReferralCode(): string | null {
  return sessionStorage.getItem(REFERRAL_CODE_STORAGE_KEY);
}

const VALID_UTM_PARAMS = [
  'utm_campaign',
  'utm_source',
  'utm_term',
  'utm_content',
  'utm_medium',
] as const;

type THubspotUTMParam = {
  name: (typeof VALID_UTM_PARAMS)[number];
  value: string;
};

function getUtmParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const utmParams: THubspotUTMParam[] = [];

  for (const param of VALID_UTM_PARAMS) {
    const paramValue = searchParams.get(param);
    if (!paramValue) continue;

    utmParams.push({
      name: param,
      value: paramValue,
    });
  }

  return utmParams;
}
